/**
 * CatalogTable — Katalog Ürünleri tablosu.
 *
 * Paylaşılan AccountingDataTable etrafında ince bir sarmalayıcı. Sayfalama ve sıralama
 * TAMAMEN sunucu taraflıdır: 23.186 kayıtta istemci tarafı dilimleme/sıralama tarayıcıyı
 * kilitler. Bu yüzden `serverSidePagination` ve `onSort` birlikte verilir.
 *
 * FİYAT SÜTUNU YOKTUR — katalog referans listesidir, satış fiyatı kullanıcının kendi
 * stoğunda oluşur.
 */

import React, { useMemo } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import Inventory2Icon from '@mui/icons-material/Inventory2';

import AccountingDataTable from '../../../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../../accounting/shared/AccountingDataTable';
import type { CatalogColumnSetting, CatalogProduct } from '../types/catalog.types';

export interface CatalogTableProps {
  rows: CatalogProduct[];
  loading: boolean;
  /** 0-tabanlı (MUI). Sıra numarası ve sayfalama bunu kullanır. */
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number, pageSize: number) => void;

  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string, direction: 'asc' | 'desc') => void;

  /** "Tabloyu Özelleştir" ayarı; görünmeyen sütunlar hiç render edilmez. */
  columnSettings: CatalogColumnSetting[];

  // ─── Toplu işlem seçimi ───────────────────────────
  selectable: boolean;
  selectedIds: number[];
  onToggleSelect: (id: number) => void;
  /** Yalnızca ekrandaki sayfanın satırlarını seçer/kaldırır. */
  onSelectPage: (ids: number[], checked: boolean) => void;

  onEdit: (row: CatalogProduct) => void;
  onDelete: (row: CatalogProduct) => void;
}

const CatalogTable: React.FC<CatalogTableProps> = ({
  rows,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  sortField,
  sortDirection,
  onSort,
  columnSettings,
  selectable,
  selectedIds,
  onToggleSelect,
  onSelectPage,
  onEdit,
  onDelete,
}) => {
  const columns = useMemo<DataTableColumn<CatalogProduct>[]>(() => {
    const byId: Record<string, DataTableColumn<CatalogProduct>> = {
      index: {
        id: 'index',
        label: '#',
        width: '60px',
        align: 'center',
        // Sunucu taraflı sayfalamada satır indeksi sayfaya göre kaydırılır.
        render: (_row, rowIndex) => (
          <Typography variant="caption" color="text.secondary">
            {page * pageSize + rowIndex + 1}
          </Typography>
        ),
      },
      name: {
        id: 'name',
        label: 'Ürün Adı',
        field: 'name',
        sortable: true,
        width: '26%',
        minWidth: '220px',
        tooltip: (row) => row.name,
        render: (row) => (
          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }} noWrap>
            {row.name}
          </Typography>
        ),
      },
      barcode: {
        id: 'barcode',
        label: 'Barkod',
        field: 'barcode',
        sortable: true,
        width: '11%',
        minWidth: '110px',
        render: (row) => row.barcode || '-',
      },
      catalogCode: {
        id: 'catalogCode',
        label: 'Katalog Kodu',
        field: 'catalogCode',
        sortable: true,
        width: '10%',
        minWidth: '100px',
        render: (row) => row.catalogCode || '-',
      },
      sectorName: {
        id: 'sectorName',
        label: 'Sektör',
        field: 'sectorName',
        sortable: true,
        width: '12%',
        minWidth: '120px',
        tooltip: (row) => row.sectorName || '-',
        render: (row) => row.sectorName || '-',
      },
      categoryPath: {
        id: 'categoryPath',
        label: 'Kategori',
        // Sunucu kategori adına göre sıralar; tabloda tam yol ("Üst > Alt") gösterilir.
        field: 'categoryName',
        sortable: true,
        width: '16%',
        minWidth: '160px',
        tooltip: (row) => row.categoryPath || 'Kategorisiz',
        render: (row) =>
          row.categoryPath || (
            <Typography variant="caption" color="text.disabled">
              Kategorisiz
            </Typography>
          ),
      },
      unit: {
        id: 'unit',
        label: 'Birim',
        field: 'unit',
        sortable: true,
        width: '80px',
        align: 'center',
        render: (row) => row.unit || '-',
      },
      vatRate: {
        id: 'vatRate',
        label: 'KDV',
        field: 'vatRate',
        sortable: true,
        width: '70px',
        align: 'center',
        render: (row) => `%${row.vatRate}`,
      },
      vatStatus: {
        id: 'vatStatus',
        label: 'KDV Durumu',
        field: 'vatStatus',
        width: '110px',
        render: (row) => row.vatStatus || '-',
      },
      departmentNo: {
        id: 'departmentNo',
        label: 'ÖKC',
        field: 'departmentNo',
        width: '70px',
        align: 'center',
        render: (row) => (row.departmentNo != null ? String(row.departmentNo) : '-'),
      },
      isActive: {
        id: 'isActive',
        label: 'Durum',
        field: 'isActive',
        sortable: true,
        width: '90px',
        align: 'center',
        render: (row) => (
          <Chip
            size="small"
            label={row.isActive ? 'Aktif' : 'Pasif'}
            color={row.isActive ? 'success' : 'default'}
            variant={row.isActive ? 'filled' : 'outlined'}
            sx={{ height: 20, fontSize: '0.68rem' }}
          />
        ),
      },
      actions: {
        id: 'actions',
        label: 'İşlemler',
        width: '100px',
        align: 'center',
        render: (row) => (
          <Box onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
            <Tooltip title="Düzenle" arrow>
              <IconButton size="small" onClick={() => onEdit(row)}>
                <EditIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sil" arrow>
              <IconButton size="small" color="error" onClick={() => onDelete(row)}>
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    };

    // Sıra ayardaki sırayla aynı; gizlenen sütunlar hiç oluşturulmaz.
    return columnSettings
      .filter((setting) => setting.visible && byId[setting.id])
      .map((setting) => byId[setting.id]);
  }, [page, pageSize, columnSettings, onEdit, onDelete]);

  const selectedAsStrings = useMemo(() => selectedIds.map(String), [selectedIds]);

  return (
    <AccountingDataTable<CatalogProduct>
      columns={columns}
      rows={rows}
      getRowId={(row) => String(row.id)}
      loading={loading}
      serverSidePagination
      totalCount={totalCount}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onSort={(field, direction) => onSort(field, direction)}
      sortField={sortField}
      sortDirection={sortDirection}
      rowsPerPageOptions={[25, 50, 100]}
      selectable={selectable}
      selectedIds={selectedAsStrings}
      onSelectId={(id) => onToggleSelect(Number(id))}
      onSelectAll={(event) => onSelectPage(rows.map((row) => row.id), event.target.checked)}
      emptyMessage="Katalogda kayıt bulunamadı."
      emptyIcon={<Inventory2Icon sx={{ fontSize: 48, color: 'text.disabled' }} />}
      columnResizeStorageKey="superadmin-catalog"
      ariaLabel="Katalog ürünleri tablosu"
    />
  );
};

export default CatalogTable;
